import Preview from "./Preview/Preview";


const HashRouteContextProps=[
    {
        name:"data",
        type:"any?",
        description:"data object passed to the route",
    },
    {
        name:"params",
        type:"[string:string]?",
        description:`
            route params object, the param name as key 
            @example
            @path <span>/:things/:name</span> params <span>{things,name}</span>
        `,
    },
    {
        name:"location",
        type:"object",
        description:"extra data about the current route location",
        props:[
            {
                name:"pathname",
                type:"string",
                description:`an URL pathname, beginning with "/"`,
            },
            {
                name:"path",
                type:"string",
                description:`The current url path, beginning with "/"`,
            },
            {
                name:"url",
                type:"string",
            },
            {
                name:"hash",
                type:"string",
                description:`an URL fragment identifier, beginning with "#"`,
            },
            {
                name:"search",
                type:"string",
                description:`an URL search string, beginning with "?"`,
            },
        ],
    },
]

const data={
    name:"data",
    type:"object",
    description:`
        Data object to pass to the new route component
        @accessible from the route component via props.data
        @You can use history/location global object for navigation, but to pass data to routes you
        need to use the methods above
    `,
}

export default {
    id:"HashRouter",
    type:"(options:object):HashRouter",
    //preview:Preview,
    description:`
        A HashRouter with nested path params support
        @Only one route is shown at a time
        @Prioritizes exact matches
        @if more than one route matches the path, the first one is picked
    `,
    options:[
        {
            name:"target",
            type:"HTMLElement",
            required:true,
            description:"Element in which the route component is inserted",
        },
        {
            name:"routes",
            type:"HashRoute[]",
            props:[
                {
                    name:"HashRoute",
                    props:[
                        {
                            name:"path",
                            type:"string",
                            required:true,
                            description:`route path, example : "/one"`,
                            tip:`
                                for path params use <b>/:</b>
                                @You can nest as many params as you need: <b>/category/:things/:name</b>, and then you can
                                access the values in the route component props.
                                @for a default route, set path to an empty string
                            `,
                        },
                        {
                            name:"memorize",
                            type:"boolean",
                            description:`
                                If true, the route component is rendered once, using the Element returned by the function
                                for next matches
                                @use it when the route is static (same UI everytime)
                            `,
                            notice:`
                                component prop must return an HTMLElement for this prop to take effect
                                @changes in path params, will trigger a rerender even when this prop is set to true
                            `,
                        },
                        {
                            name:"guard",
                            type:"(context:object):void",
                            description:"if specified, the route is protected and locked until explicitly allowed",
                            props:[{
                                name:"context",
                                type:"object",
                                props:[
                                    {
                                        name:"allow",
                                        type:"():void",
                                        description:"allows navigation to the route",
                                    },
                                    {
                                        name:"redirect",
                                        type:"(to?:string,data?:any):void",
                                    },
                                    {
                                        name:"target",
                                        type:"HTMLElement",
                                        description:"same as the target prop value",
                                    },
                                    {name:"data",type:"any?"},
                                    {name:"params",type:"[string:string]?"},
                                ],
                            }],
                        },
                        {
                            name:"component",
                            type:"(props:object):HashRouteElement|Promise<HashRouteElement>,",
                            required:true,
                            props:[
                                {
                                    name:"props",
                                    type:"object",
                                    props:[
                                        {
                                            name:"parent",
                                            type:"HTMLElement",
                                            description:"the target prop value",
                                        },
                                        ...HashRouteContextProps,
                                    ],
                                },
                                {
                                    name:"HashRouteElement",
                                    type:"HTMLElement",
                                    props:[
                                        {
                                            name:"onShow",
                                            type:"(context:object):void",
                                            description:`
                                                a method attached to the element that 
                                                is called every time the route is shown
                                            `,
                                            props:[{
                                                name:"context",
                                                type:"object",
                                                props:HashRouteContextProps,
                                            }],
                                        },
                                        {
                                            name:"onHide",
                                            type:"():void",
                                            description:`
                                                a method attached to the element that 
                                                is called every time the route is hidden
                                            `,
                                        },
                                    ],
                                }
                            ],
                        },
                    ],
                }
            ],
        },
    ],
    methods:[
        {
            name:"push",
            type:"(path:string,data:object):void",
            description:"Adds an entry to the browser's session history stack",
        },
        {
            name:"append",
            type:"(path:string,data:object):void",
            description:"appends the path to the end of the current path",
        },
        {
            name:"replace",
            type:"(path:string,data:object):void",
            description:"replaces the current history entry",
            props:[data],
        },
        {
            name:"back",
            type:"(data?:any):void",
        },
        {
            name:"refresh",
            type:"():void",
            description:"rerenders the current route even if memorize true is specified",
        },
        {
            name:"reset",
            type:"():void",
            description:"resets all routes",
        },
    ].map((it,i,{length})=>({
        ...it,
        notice:(i+1===length)&&`
            for other navigation options, use the 
            <a href="https://developer.mozilla.org/en-US/docs/Web/API/History">History API</a>
        `,
    })),
};
